import { useQuery } from '@tanstack/react-query';
import { DataTable, EmptyState, Badge } from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { apiClientPaginated } from '@/lib/api-client';

// ─── Design tokens ───────────────��──────────────────────────────────────────
const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

interface LocationRow {
  id: string;
  name: string;
  address?: string;
  city?: string;
  is_active: boolean;
  user_count?: number;
  created_at: string;
}

const columns: ColumnDef<LocationRow>[] = [
  { key: 'name', header: 'Name' },
  {
    key: 'city',
    header: 'City',
    render: (v) => (v as string) || '—',
  },
  {
    key: 'is_active',
    header: 'Status',
    render: (v) => (
      <Badge variant={(v as boolean) ? 'success' : 'danger'}>
        {(v as boolean) ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
  {
    key: 'user_count',
    header: 'Users',
    numeric: true,
    render: (v) => <span className="tabular-nums">{(v as number) ?? 0}</span>,
  },
  {
    key: 'created_at',
    header: 'Created',
    render: (v) => (
      <span className="tabular-nums text-xs text-neutral-500 dark:text-neutral-400">
        {new Date(v as string).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
      </span>
    ),
  },
];

interface TenantLocationsTabProps {
  tenantId: string;
}

export function TenantLocationsTab({ tenantId }: TenantLocationsTabProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['tenant', tenantId, 'locations'],
    queryFn: () => apiClientPaginated<LocationRow[]>(`/locations?tenant_id=${tenantId}&limit=50`),
  });

  const locations = data?.data ?? [];

  if (!isLoading && locations.length === 0) {
    return (
      <div className="animate-slide-up">
        <EmptyState
          variant="page"
          title="No locations added yet"
          description="Locations will appear here once the tenant adds their first store"
        />
      </div>
    );
  }

  return (
    <div className={`${GLASS_PANEL} overflow-hidden animate-slide-up transition-shadow duration-200 hover:shadow-lg`}>
      <DataTable
        columns={columns}
        data={locations}
        loading={isLoading}
        variant="default"
        emptyState={{ title: 'No locations', description: 'No locations found for this tenant' }}
      />
    </div>
  );
}
